import React from 'react';
import { useFeatureAccess } from '../../hooks/useQuota';
import { PricingTier } from '../../types';
import { QUOTA_ITEMS_CONFIG, getQuotaValues } from '../../utils/quotaConfig';
import QuotaItem from './QuotaItem';
import './QuotaDisplay.css';

interface QuotaDisplayProps {
  variant?: 'compact' | 'detailed';
  showUpgradePrompt?: boolean;
  className?: string;
}

const QuotaDisplay: React.FC<QuotaDisplayProps> = ({ 
  variant = 'compact', 
  showUpgradePrompt = true,
  className = '' 
}) => {
  const { quota, tier, getUsagePercentage } = useFeatureAccess();

  if (!quota) {
    return (
      <div className={`quota-display loading ${className}`}>
        <div className="quota-loading-spinner"></div>
        <span>Loading quotas...</span>
      </div>
    );
  }


  const renderUpgradePrompt = () => {
    if (!showUpgradePrompt || tier === PricingTier.Pro) return null;

    const hasLimits = QUOTA_ITEMS_CONFIG.some(item => {
      const { max } = getQuotaValues(quota, item.key);
      return max !== -1;
    });

    if (!hasLimits) return null;

    return (
      <div className="quota-upgrade-prompt">
        <div className="quota-upgrade-icon">🚀</div>
        <div className="quota-upgrade-text">
          <strong>Need more?</strong>
          <a href="/pricing" className="quota-upgrade-link">
            Upgrade to {tier === PricingTier.Free ? 'Basic' : 'Pro'}
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className={`quota-display ${variant} ${className}`}>
      <div className="quota-header-section">
        <h4 className="quota-title">
          {variant === 'compact' ? 'Usage' : 'Quota Usage'}
        </h4>
        {variant === 'detailed' && (
          <div className="quota-tier-badge">
            {tier} Plan
          </div>
        )}
      </div>
      
      <div className="quota-items">
        {QUOTA_ITEMS_CONFIG.map(item => (
          <QuotaItem
            key={item.key}
            config={item}
            quota={quota}
            variant={variant}
            getUsagePercentage={getUsagePercentage}
          />
        ))}
      </div>
      
      {renderUpgradePrompt()}
    </div>
  );
};

export default QuotaDisplay;
